import { EmptyState } from '../../../components/ui';
import Box from './Box';
import Badge from '../../../components/ui/Badge';
import { FaShieldAlt } from '../../../components/ui/icons';
import RiskSignalBars from './RiskSignalBars';
import type { RiskBlock } from '../../../api/types';

interface Props { risk: RiskBlock | null; }

const TONE_BY_BAND: Record<RiskBlock['band'], 'success' | 'warning' | 'danger'> = {
  low:    'success',
  medium: 'warning',
  high:   'danger',
};

function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export default function RiskScorePanel({ risk }: Props) {
  if (risk === null) {
    return (
      <Box title="Risk score" help="risk_score">
        <EmptyState compact
          icon={<FaShieldAlt />}
          title="Computed nightly"
          body="The risk score is calculated from recent trend, engagement signals, and weak-chapter exposure. Shown here when the nightly rollup populates it." />
      </Box>
    );
  }

  return (
    <Box title="Risk score" help="risk_score"
      actions={<Badge tone={TONE_BY_BAND[risk.band]} size="sm">{risk.score.toFixed(0)} · {risk.band.charAt(0).toUpperCase() + risk.band.slice(1)}</Badge>}>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[44px] font-semibold leading-none tracking-[-0.04em] text-ink">{risk.score.toFixed(0)}</span>
        <span className="text-muted">/ 100</span>
      </div>
      <p className="mb-2 mt-2.5 text-[13px] text-muted">What is driving it, in points:</p>
      <RiskSignalBars signals={risk.signals} />
      {risk.last_computed_at && (
        <div className="mt-2 text-xs text-muted">
          Last computed {timeAgo(risk.last_computed_at)}
        </div>
      )}
    </Box>
  );
}
